import { VehicleController } from 'core/repositories/VehicleController';
import { Gear, Blinker, ControlMode, EmergencyMode } from 'core/models/drive';
import { GamepadAction, GamepadAxis, axis, button, and, or } from './GamepadPrimitives';

type GamepadSettings = {
    id: string;
    steeringWheel: GamepadAction;
    accelPedal: GamepadAction;
    brakePedal: GamepadAction;
    hazardLightSwitch: GamepadAction;
    leftBlinkerSwitch: GamepadAction;
    rightBlinkerSwitch: GamepadAction;
    emergencyButton: GamepadAction;
    controlModeButton: GamepadAction;
    parkGear: GamepadAction;
    neutralGear: GamepadAction;
    driveGear: GamepadAction;
    reverseGear: GamepadAction;
    SUBSCRIBE_INTERVAL: number;
}

type UnsupportedGamepadSettings = {
    id: string;
    message?: string;
}

function isSupported(settings: GamepadSettings | UnsupportedGamepadSettings): settings is GamepadSettings {
    return 'steeringWheel' in settings;
}

const knownControllers: (GamepadSettings | UnsupportedGamepadSettings)[] = [
    // G29 Driving Force Racing Wheel (PS3 Mode only)
    {
        // Chrome on Windows
        id: 'G29 Driving Force Racing Wheel (Vendor: 046d Product: c24f)',
        steeringWheel: axis(0, [-1.0, 1.0]),
        accelPedal: axis(2, [1.0, -1.0]),
        brakePedal: axis(5, [1.0, -1.0]),
        hazardLightSwitch: button(3),
        leftBlinkerSwitch: or(button(5), button(1)),
        rightBlinkerSwitch: or(button(4), button(2)),
        emergencyButton: and(button(23), button(10)),
        controlModeButton: and(button(24), button(10)),
        parkGear: and(axis(9, [-0.71429, -1.0000], 'min'), button(10)),
        driveGear: and(axis(9, [0.42857, 0.14286], 'min'), button(10)),
        neutralGear: and(axis(9, [1.00000, 0.71429], 'min'), button(10)),
        reverseGear: and(axis(9, [0.14286, -0.42857], 'min'), button(10)),
        SUBSCRIBE_INTERVAL: 50
    },
    {
        // Chrome/Firefox on macOS
        // PS4 Mode: 'G29 Driving Force Racing Wheel (Vendor: 046d Product: c260)'
        // PS3 Mode: 'G29 Driving Force Racing Wheel (Vendor: 046d Product: c294)'
        id: 'G29 Driving Force Racing Wheel',
        steeringWheel: axis(0, [-1.0, 1.0]),
        accelPedal: axis(1, [0, -1.0]),
        brakePedal: axis(1, [0, 1.0]),
        hazardLightSwitch: button(3),
        leftBlinkerSwitch: button(0),
        rightBlinkerSwitch: button(2),
        emergencyButton: and(button(12), button(11)),
        controlModeButton: and(button(4), button(5)),
        parkGear: and(axis(9, [-0.71429, -1.0000], 'min'), button(11)),
        driveGear: and(axis(9, [0.42857, 0.14286], 'min'), button(11)),
        neutralGear: and(axis(9, [1.00000, 0.71429], 'min'), button(11)),
        reverseGear: and(axis(9, [0.14286, -0.42857], 'min'), button(11)),
        SUBSCRIBE_INTERVAL: 50
    },
    {
        // Safari on macOS
        // PS4 Mode: 'G29 Driving Force Racing Wheel (Vendor: 046d Product: c260)'
        // PS3 Mode: 'G29 Driving Force Racing Wheel (Vendor: 046d Product: c294)'
        id: '46d-c294-G29 Driving Force Racing Wheel',
        steeringWheel: axis(0, [-1.0, 1.0]),
        accelPedal: axis(1, [0, -1.0]),
        brakePedal: axis(1, [0, 1.0]),
        hazardLightSwitch: button(3),
        leftBlinkerSwitch: button(4),
        rightBlinkerSwitch: button(5),
        emergencyButton: and(button(23), button(11)), // not supported
        controlModeButton: and(button(12), button(11)),
        parkGear: and(button(13), button(11)),
        driveGear: and(button(14), button(11)),
        neutralGear: and(button(16), button(11)),
        reverseGear: and(button(15), button(11)),
        SUBSCRIBE_INTERVAL: 50
    },
    {
        // Chrome on ubuntu
        // PS4 Mode: 'Logitech G29 Driving Force Racing Wheel (Vendor: 046d Product: c260)'
        // PS3 Mode: 'Logitech G29 Driving Force Racing Wheel (Vendor: 046d Product: c24f)'
        id: 'Logitech G29 Driving Force Racing Wheel (Vendor: 046d Product: c24f)',
        steeringWheel: axis(0, [-1.0, 1.0]),
        accelPedal: axis(2, [1.0, -1.0]),
        brakePedal: axis(3, [1.0, -1.0]),
        hazardLightSwitch: button(3),
        leftBlinkerSwitch: or(button(5), button(1)),
        rightBlinkerSwitch: or(button(4), button(2)),
        emergencyButton: and(button(23), button(10)),
        controlModeButton: and(button(24), button(10)),
        parkGear: and(axis(5, [0, -1.0]), button(10)),
        driveGear: and(axis(5, [0, 1.0]), button(10)),
        neutralGear: and(axis(4, [0, 1.0]), button(10)),
        reverseGear: and(axis(4, [0, -1.0]), button(10)),
        SUBSCRIBE_INTERVAL: 50
    },
    {
        // Firefox on ubuntu
        // PS4 Mode: '046d-c260-Logitech G29 Driving Force Racing Wheel'
        // PS3 Mode: '046d-c24f-Logitech G29 Driving Force Racing Wheel'
        id: '046d-c24f-Logitech G29 Driving Force Racing Wheel',
        steeringWheel: axis(0, [-1.0, 1.0]),
        accelPedal: axis(2, [1.0, -1.0]),
        brakePedal: axis(3, [1.0, -1.0]),
        hazardLightSwitch: button(3),
        leftBlinkerSwitch: button(5),
        rightBlinkerSwitch: button(4),
        emergencyButton: and(button(23), button(10)),
        controlModeButton: and(button(24), button(10)),
        parkGear: and(axis(5, [0, -1.0]), button(10)),
        driveGear: and(axis(5, [0, 1.0]), button(10)),
        neutralGear: and(axis(4, [0, 1.0]), button(10)),
        reverseGear: and(axis(4, [0, -1.0]), button(10)),
        SUBSCRIBE_INTERVAL: 50
    },

    // Thrustmaster Racing Wheel T300RS with tmdrv (PS3 Mode only)
    {
        // Chrome on ubuntu
        id: 'Thrustmaster TRS Racing Wheel (Vendor: 044f Product: b65e)',
        steeringWheel: axis(0, [-1.0, 1.0]),
        accelPedal: axis(2, [1.0, -1.0]),
        brakePedal: axis(1, [1.0, -1.0]),
        hazardLightSwitch: button(2),
        leftBlinkerSwitch: or(button(3), button(9)),
        rightBlinkerSwitch: or(button(4), button(8)),
        emergencyButton: and(button(12), button(7)),
        controlModeButton: and(button(0), button(1)),
        parkGear: and(axis(5, [0, -1.0]), button(7)),
        driveGear: and(axis(5, [0, 1.0]), button(7)),
        neutralGear: and(axis(4, [0, 1.0]), button(7)),
        reverseGear: and(axis(4, [0, -1.0]), button(7)),
        SUBSCRIBE_INTERVAL: 50
    },

    {
        // Chrome on Windows
        id: 'B66E (Vendor: 044f Product: b66e)',
        steeringWheel: axis(0, [-1.0, 1.0]),
        accelPedal: axis(5, [1.0, -1.0]),
        brakePedal: axis(1, [1.0, -1.0]),
        hazardLightSwitch: button(2),
        leftBlinkerSwitch: or(button(3), button(9)),
        rightBlinkerSwitch: or(button(4), button(8)),
        emergencyButton: and(button(12), button(7)),
        controlModeButton: and(button(0), button(1)),
        parkGear: and(axis(9, [-0.71429, -1.0000], 'min'), button(7)),
        driveGear: and(axis(9, [0.42857, 0.14286], 'min'), button(7)),
        neutralGear: and(axis(9, [1.00000, 0.71429], 'min'), button(7)),
        reverseGear: and(axis(9, [0.14286, -0.42857], 'min'), button(7)),
        SUBSCRIBE_INTERVAL: 50
    },

    // Thrustmaster Racing Wheel T300RS w/o tmdrv (PS3 Mode only)
    {
        // Chrome on ubuntu
        id: 'Thrustmaster Thrustmaster Racing Wheel FFB (Vendor: 044f Product: b66d)',
        message: 'Use https://gitlab.com/her0/tmdrv/-/issues/4 to enable pedals'
    },
    {
        // Firefox on ubuntu
        id: '044f-b66d-Thrustmaster Thrustmaster Racing Wheel FFB',
        message: 'Use https://gitlab.com/her0/tmdrv/-/issues/4 to enable pedals'
    },

    // Other controllers might follow here...
];

const GAMEPAD_SCAN_INTERVAL = 10; // [ms]

function normalizeAxisValue(gamepad: Gamepad, axis: GamepadAxis) {
    const [min, max] = axis.valueRange;
    const rawValue = gamepad.axes[axis.index] ?? min;
    const normalized = (rawValue - min) / (max - min);
    switch (axis.outOfBounds) {
        case 'minmax':
            return Math.min(1, Math.max(0, normalized));
        case 'min':
            if (normalized > 1.0 + 0.1) return 0.0;
            return Math.min(1, Math.max(0, normalized));
        case 'max':
            if (normalized < 0.0 - 0.1) return 1.0;
            return Math.min(1, Math.max(0, normalized));
        case 'asis':
            return normalized;
    }
}

function booleanValue(gamepad: Gamepad, action: GamepadAction): boolean {
    switch (action.type) {
        case 'and':
            return action.actions.every(action => booleanValue(gamepad, action));
        case 'or':
            return action.actions.some(action => booleanValue(gamepad, action));
        case 'not':
            return !booleanValue(gamepad, action.action);
        case 'button':
            return gamepad.buttons[action.index]?.pressed ?? false;
        case 'axis':
            return normalizeAxisValue(gamepad, action) > 0.5;
    }
}

function normalizedValue(gamepad: Gamepad, action: GamepadAction): number {
    switch (action.type) {
        case 'and':
            return action.actions.map(action => normalizedValue(gamepad, action)).reduce((a, v) => a * v, 0);
        case 'or':
            return action.actions.map(action => normalizedValue(gamepad, action)).reduce((a, v) => a + v, 0);
        case 'not':
            return booleanValue(gamepad, action.action) ? 0 : 1;
        case 'button':
            return booleanValue(gamepad, action) ? 1 : 0;
        case 'axis':
            return normalizeAxisValue(gamepad, action);
    }
}

function oneof<Key, T>(actions: Map<Key, (gamepad: Gamepad) => boolean>, defaultValue: T) {
    return (gamepad: Gamepad) => {
        const hits = [...actions.entries()]
            .map(([k, f]) => [k, f(gamepad)] as const)
            .reduce(
                (acc, [k, v]) => v ? [...acc, k] : acc,
                [] as Key[]);
        return (hits.length === 1) ? hits[0] : defaultValue;
    };
}

class AnalogState<T = number> {
    private _scan: (gamepad: Gamepad) => T;
    private onChange: (value: T) => void;

    constructor(scan: (gamepad: Gamepad) => T, onChange: (value: T) => void) {
        this._scan = scan;
        this.onChange = onChange;
    }

    scan(gamepad: Gamepad) {
        return this._scan(gamepad);
    }

    test(gamepad: Gamepad) {
        this.update(this.scan(gamepad));
    }

    update(newValue: T) {
        this.onChange(newValue);
    }
}

function analogState<T>(scan: (gamepad: Gamepad) => T, onChange: (value: T) => void) {
    return new AnalogState<T>(scan, onChange);
}

class ReduceState<U, T> {
    private _scan: (gamepad: Gamepad) => U;
    private _interpret: (input: U, lastValue: T) => T;
    private onChange: (value: T) => void;
    private currentValue: T;

    constructor(scan: (gamepad: Gamepad) => U, interpret: (input: U, lastValue: T) => T, onChange: (value: T) => void, initValue: T) {
        this._scan = scan;
        this._interpret = interpret;
        this.onChange = onChange;
        this.currentValue = initValue;
    }

    scan(gamepad: Gamepad) {
        return this._scan(gamepad);
    }

    test(gamepad: Gamepad) {
        const input = this.scan(gamepad);
        const nextValue = this._interpret(input, this.currentValue);
        this.update(nextValue);
    }

    update(newValue: T) {
        const prevValue = this.currentValue;
        this.currentValue = newValue;

        if (prevValue != newValue) {
            this.onChange(newValue);
        }
    }
}

function reduceState<U, T>(interpret: (input: U, lastValue: T) => T, initValue: T, scan: (gamepad: Gamepad) => U, onChange: (value: T) => void) {
    return new ReduceState<U, T>(scan, interpret, onChange, initValue);
}

function boolean(action: GamepadAction) {
    return (gamepad: Gamepad) => booleanValue(gamepad, action);
}

function upedge(scan: (gamepad: Gamepad) => boolean): (gamepad: Gamepad) => boolean {
    let currentValue: boolean | undefined;
    return gamepad => {
        const newValue = scan(gamepad);
        const prevValue = currentValue;
        currentValue = newValue;
        if (prevValue === undefined || prevValue != newValue) {
            return newValue;
        } else {
            return false;
        }
    };
}

type GamepadStates = {
    steer: AnalogState;
    accel: AnalogState;
    brake: AnalogState;
    blinker: ReduceState<Blinker | null, Blinker | null>;
    gear: ReduceState<Gear | null, Gear>;
    controlMode: ReduceState<boolean, ControlMode>;
    emergency: ReduceState<boolean, EmergencyMode>;
}

export class GamingWheelVehicleController implements VehicleController {
    private interval: number | undefined;
    private gamepads = new Map<Gamepad, GamepadStates>();

    private listeners: VehicleController.EventListener[] = [];

    private notify(event: VehicleController.Event) {
        [...this.listeners].forEach(listener => {
            listener(event);
        });
    }

    constructor() {
        window.addEventListener('gamepadconnected', (e: Event) => {
            if (e instanceof GamepadEvent) {
                this.bindGamepad(e.gamepad);
            }
        });

        window.addEventListener('gamepaddisconnected', (e: Event) => {
            if (e instanceof GamepadEvent) {
                this.unbindGamepad(e.gamepad);
            }
        });

        [...navigator.getGamepads()].forEach(gamepad => {
            if (!gamepad) return;
            this.bindGamepad(gamepad);
        });
    }

    private bindGamepad(gamepad: Gamepad) {
        const settings = knownControllers.find(x => gamepad.id.startsWith(x.id));
        if (!settings) {
            console.warn(`unsupported gamepad: ${gamepad.id}`);
            return;
        }
        if (!isSupported(settings)) {
            console.warn(`unsupported gamepad: ${gamepad.id}`, settings.message);
            return;
        }

        if (this.gamepads.has(gamepad)) return;

        this.gamepads.set(
            gamepad,
            {
                steer: analogState(
                    gamepad => normalizedValue(gamepad, settings.steeringWheel) * 2 - 1.0,
                    steering => this.notify({ eventType: 'steering', steering })),
                accel: analogState(
                    gamepad => normalizedValue(gamepad, settings.accelPedal),
                    accel => this.notify({ eventType: 'accel', accel })),
                brake: analogState(
                    gamepad => normalizedValue(gamepad, settings.brakePedal),
                    brake => this.notify({ eventType: 'brake', brake })),
                blinker: reduceState(
                    (input, last) => {
                        const current = last ?? null;
                        return input != null
                            ? input == last ? null : input
                            : current;
                    },
                    null as Blinker | null,
                    oneof(new Map([
                        [Blinker.Hazard, upedge(boolean(settings.hazardLightSwitch))],
                        [Blinker.Left, upedge(boolean(settings.leftBlinkerSwitch))],
                        [Blinker.Right, upedge(boolean(settings.rightBlinkerSwitch))],
                    ]), null),
                    blinker => {
                        this.notify({ eventType: 'blinker', blinker });
                    }),
                gear: reduceState(
                    (input, last) => {
                        const current = last ?? Gear.P;
                        return input ?? current;
                    },
                    Gear.P as Gear,
                    oneof(new Map([
                        [Gear.P, boolean(settings.parkGear)],
                        [Gear.N, boolean(settings.neutralGear)],
                        [Gear.D, boolean(settings.driveGear)],
                        [Gear.R, boolean(settings.reverseGear)],
                    ]), null),
                    gear => {
                        if (gear != null) {
                            this.notify({ eventType: 'gear', gear });
                        }
                    }),
                controlMode: reduceState(
                    (pressed, last) => {
                        const current = last ?? ControlMode.Auto;
                        return pressed
                            ? current === ControlMode.Auto ? ControlMode.Remote : ControlMode.Auto
                            : current;
                    },
                    ControlMode.Auto as ControlMode,
                    upedge(boolean(settings.controlModeButton)),
                    controlMode => this.notify({ eventType: 'controlMode', controlMode })),
                emergency: reduceState(
                    (pressed, last) => {
                        const current = last ?? EmergencyMode.Normal;
                        return pressed
                            ? current === EmergencyMode.Normal ? EmergencyMode.Emergency : EmergencyMode.Normal
                            : current;
                    },
                    EmergencyMode.Normal as EmergencyMode,
                    upedge(boolean(settings.emergencyButton)),
                    emergency => this.notify({ eventType: 'emergency', emergency })),
            });
        console.info(`found a steering wheel: ${gamepad.id}`);

        if (!this.interval) {
            this.interval = window.setInterval(() => {
                this.update();
            }, GAMEPAD_SCAN_INTERVAL);
        }
    }

    private unbindGamepad(gamepad: Gamepad) {
        if (!this.gamepads.has(gamepad)) return;

        this.gamepads.delete(gamepad);

        if (this.gamepads.size == 0) {
            clearInterval(this.interval);
            this.interval = undefined;
        }
    }

    private update() {
        const currentGamepads = navigator.getGamepads();
        this.gamepads.forEach((states, key) => {
            const gamepad = currentGamepads[key.index];
            if (!gamepad) return;

            states.emergency.test(gamepad);
            states.controlMode.test(gamepad);
            states.gear.test(gamepad);
            states.blinker.test(gamepad);
            states.brake.test(gamepad);
            states.accel.test(gamepad);
            states.steer.test(gamepad);
        });
    }

    addEventListener(listener: VehicleController.EventListener): void {
        this.listeners.push(listener);
    }

    removeEventListener(listener: VehicleController.EventListener): void {
        const index = this.listeners.indexOf(listener);
        if (index < 0) return;

        this.listeners.splice(index, 1);
    }

    pushEvent(event: VehicleController.Event) {
        switch (event.eventType) {
            case 'blinker':
                this.gamepads.forEach(states => {
                    states.blinker.update(event.blinker);
                });
                break;

            case 'controlMode':
                console.log(`input controlMode: ${event.controlMode}`);
                this.gamepads.forEach(states => {
                    states.controlMode.update(event.controlMode);
                });
                break;

            case 'emergency':
                this.gamepads.forEach(states => {
                    states.emergency.update(event.emergency);
                });
                break;
        }

        this.notify(event);
    }
}
